import { createNanoEvents } from "nanoevents";

// G7-1: session-scoped signal for the home "new achievement" prompt.
//
// Persisted achievements (idb.meta "achievements") carry only {slug, difficulty} —
// no timestamp, no season — so "newly unlocked" is NOT judgeable from storage across
// sessions (reported as a data-availability finding). What IS cheap and true is the
// SESSION signal: the worker fires a `type: "achievement"` logEvent each time one is
// awarded, and the UI counts those here. Combined with "a celebration moment was
// just closed", the home surface can show a one-time "新达成成就 N 项·查看" entry
// (opens the AchievementsSheet) after the dust settles on a title/milestone.
//
// sessionStorage keeps the two signals across an in-session refresh; the emitter
// lets an already-mounted home view react the moment a celebration is closed (rather
// than only on its next mount). Nothing here touches the navigation back stack.

const ACHIEVEMENTS_KEY = "m_achievements_session";
const CELEBRATION_KEY = "m_celebration_seen";

export const achievementPromptEmitter = createNanoEvents<{
	change: () => void;
}>();

const getSessionStorage = (): Storage | undefined => {
	try {
		if (typeof window === "undefined" || !window.sessionStorage) {
			return undefined;
		}
		return window.sessionStorage;
	} catch {
		// Access can throw in privacy modes / sandboxed frames.
		return undefined;
	}
};

const readCount = (storage: Storage): number => {
	const raw = storage.getItem(ACHIEVEMENTS_KEY);
	const n = raw ? Number.parseInt(raw, 10) : 0;
	return Number.isFinite(n) && n > 0 ? n : 0;
};

// One achievement was awarded this session (called from the UI logEvent pipeline
// on every `type: "achievement"` event, regardless of whether its toast shows).
export const recordAchievementUnlocked = (): void => {
	const storage = getSessionStorage();
	if (!storage) {
		return;
	}
	try {
		storage.setItem(ACHIEVEMENTS_KEY, String(readCount(storage) + 1));
		achievementPromptEmitter.emit("change");
	} catch {
		// Quota / privacy failure degrades to "no prompt" — acceptable, transient.
	}
};

// A celebration moment (championship / milestone) was closed this session.
export const markCelebrationSeen = (): void => {
	const storage = getSessionStorage();
	if (!storage) {
		return;
	}
	try {
		storage.setItem(CELEBRATION_KEY, "1");
		achievementPromptEmitter.emit("change");
	} catch {
		// ignore
	}
};

// The prompt shows only when a celebration was closed this session AND at least one
// achievement was unlocked. Returns the count (>0) or undefined.
export const readAchievementPrompt = (): number | undefined => {
	const storage = getSessionStorage();
	if (!storage) {
		return undefined;
	}
	if (storage.getItem(CELEBRATION_KEY) !== "1") {
		return undefined;
	}
	const count = readCount(storage);
	return count > 0 ? count : undefined;
};

// One-time: consumed on view/dismiss so it never re-appears ("关闭即消").
export const clearAchievementPrompt = (): void => {
	const storage = getSessionStorage();
	if (!storage) {
		return;
	}
	try {
		storage.removeItem(ACHIEVEMENTS_KEY);
		storage.removeItem(CELEBRATION_KEY);
		achievementPromptEmitter.emit("change");
	} catch {
		// ignore
	}
};
